import { FC, useCallback, useEffect, useMemo, useState } from "react";
import OrderOptimizerRailyDetailsHiddenDetailsDriver from "./types/order-optimizer-raily-details-hidden-details-driver";
import OrderOptimizerRailyDetailsHiddenDetailsDriverSelectOption from "./types/order-optimizer-raily-details-hidden-details-driver-select-option";
import orderOptimizerRailyDetailsHiddenDetailsDriverListFactory from "./factory/order-optimizer-raily-details-hidden-details-driver-list.factory";
import orderOptimizerRailyDetailsHiddenDetailsDriverFactory from "./factory/order-optimizer-raily-details-hidden-details-driver.factory";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import ButtonComponent from "../../../../../../common/components/button/button.component";
import SingleSelectComponent from "../../../../../../common/components/form/select/single-select/single-select.component";
import useAbort from "../../../../../../common/hooks/use-abort";
import OrderOptimizerPotentialDriversListParams from "../../../../../../common/services/order-optimizer/potential-drivers-list/order-optimizer-potential-drivers-list-params";
import useOrderOptimizerPotentialDriversList from "../../../../../../common/services/order-optimizer/potential-drivers-list/use-order-optimizer-potential-drivers-list";
import orderOptimizerTranslationsHelper from "../../../../../../languages/order-optimizer-translations.helper";
import debounce from "lodash/debounce";

type OrderOptimizerRailyDetailsHiddenDetailsDriverSelectProps = {
  optimizationId: string | undefined;
  onPotentialDriverChange: (
    driver: OrderOptimizerRailyDetailsHiddenDetailsDriver | null,
  ) => void;
};

const OrderOptimizerRailyDetailsHiddenDetailsDriverSelectComponent: FC<
  OrderOptimizerRailyDetailsHiddenDetailsDriverSelectProps
> = (props) => {
  const translations =
    orderOptimizerTranslationsHelper.getRailyDetailsTranslations().table
      .headingTables.potentialDriver;

  const [potentialDriver, setPotentialDriver] =
    useState<OrderOptimizerRailyDetailsHiddenDetailsDriver | null>(null);

  const [potentialDriverSearchQuery, setPotentialDriverSearchQuery] =
    useState("");
  const [potentialDriverSelectOptions, setPotentialDriversSelectOptions] =
    useState<OrderOptimizerRailyDetailsHiddenDetailsDriverSelectOption[]>([]);

  const potentialDriverList = useOrderOptimizerPotentialDriversList();
  const potentialDriverListAbort = useAbort();

  const loadPotentialDriverListDebounced = useCallback(
    debounce(() => {
      if (!props.optimizationId) return;

      const loadParams: OrderOptimizerPotentialDriversListParams = {
        optimizationId: props.optimizationId,
        name: !!potentialDriverSearchQuery
          ? potentialDriverSearchQuery
          : undefined,
      };

      return potentialDriverList.load(
        loadParams,
        potentialDriverListAbort.signal,
      );
    }, 500),
    [potentialDriverSearchQuery],
  );

  useEffect(() => {
    loadPotentialDriverListDebounced();

    return () => {
      potentialDriverListAbort.revoke();
      loadPotentialDriverListDebounced.cancel();
    };
  }, [props.optimizationId, potentialDriverSearchQuery]);

  useEffect(() => {
    const potentialDriversList =
      orderOptimizerRailyDetailsHiddenDetailsDriverListFactory.createPotentialDriverList(
        potentialDriverList.data,
      );

    const options: OrderOptimizerRailyDetailsHiddenDetailsDriverSelectOption[] =
      orderOptimizerRailyDetailsHiddenDetailsDriverFactory.createPotentialDriverSelectOptions(
        potentialDriversList,
      );

    setPotentialDriversSelectOptions(options);
  }, [potentialDriverList.data]);

  const selectedPotentialDriverSelectOption = useMemo(() => {
    if (!potentialDriver) {
      return null;
    }

    return orderOptimizerRailyDetailsHiddenDetailsDriverFactory.createPotentialDriverSelectOption(
      potentialDriver,
    );
  }, [potentialDriver]);

  const isPotentialDriverListLoading =
    !!props.optimizationId && potentialDriverList.isLoading;

  const confirmPotentialDriver = debounce((clearDriver?: boolean) => {
    if (clearDriver && !potentialDriver) return;
    props.onPotentialDriverChange(clearDriver ? null : potentialDriver);
  }, 500);

  return (
    <div className="order_optimizer_raily_details_filter">
      <div className="order_optimizer_raily_details_filter__header">
        <div className="order_optimizer_raily_details_filter__header__text">
          {translations.headingLabel}
        </div>
      </div>
      <div className="order_optimizer_raily_details_filter__select">
        <SingleSelectComponent
          placeholder={translations.selectPlaceholder}
          value={selectedPotentialDriverSelectOption}
          onChange={(
            value: OrderOptimizerRailyDetailsHiddenDetailsDriverSelectOption | null,
          ) => {
            if (!value) {
              setPotentialDriver(null);
              confirmPotentialDriver(true);
            } else {
              setPotentialDriver(value.value);
            }
          }}
          isLoading={isPotentialDriverListLoading}
          options={potentialDriverSelectOptions}
          isSearchable
          isClearable
          filterFunction={() => true}
          inputValue={potentialDriverSearchQuery}
          onInputChange={setPotentialDriverSearchQuery}
          noOptionsMessage={(inputValue) => {
            if (inputValue) {
              return translations.searchNoOptionsMessage;
            }
            return translations.searchTipMessage;
          }}
          iconRight={false}
        />
        <ButtonComponent
          classNames={{
            root: "order_optimizer_raily_details_filter__select__input_wrapper__button",
          }}
          onClick={() => confirmPotentialDriver()}
          title={translations.selectPlaceholder}
          type="primary"
        >
          <FontAwesomeIcon
            className="order_optimizer_raily_details_filter__select__input_wrapper__button__icon"
            icon={faMagnifyingGlass}
          />
        </ButtonComponent>
      </div>
    </div>
  );
};

export default OrderOptimizerRailyDetailsHiddenDetailsDriverSelectComponent;
